import React, { useState, useEffect, useMemo } from "react";
import { useTheme } from "styled-components";
import api from "../../../services/api";
import { Container } from "./styles";
import SupplierDetailLoadingSkeleton from "./SupplierDetailLoadingSkeleton";
import {
  FiArrowLeft,
  FiPackage,
  FiDollarSign,
  FiShoppingCart,
  FiTrendingUp,
  FiPercent,
  FiLayers,
  FiActivity,
  FiFileText,
  FiTag,
  FiCalendar,
  FiLoader,
  FiRefreshCw,
} from "react-icons/fi";
import { GoLaw, GoGraph } from "react-icons/go";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";

export default function SupplierDetail({ fornecedor, onVoltar, initialTab = "geral" }) {
  const theme = useTheme();

  // Estados
  const [activeTab, setActiveTab] = useState(initialTab);
  const [loading, setLoading] = useState(true);
  const [detailData, setDetailData] = useState(null);

  // Buscar detalhes do fornecedor da API
  useEffect(() => {
    const fetchDetails = async () => {
      try {
        setLoading(true);
        const projectId = localStorage.getItem("vidal_selected_project");
        const supplierId = fornecedor?.id || fornecedor?.codigo;

        if (!projectId || !supplierId) {
          console.warn("ProjectId ou SupplierId não encontrado");
          setLoading(false);
          return;
        }

        const response = await api.get(
          `/suppliers/${projectId}/detail/${supplierId}`
        );

        if (response.data && response.data.success) {
          setDetailData(response.data.data);
        }
      } catch (error) {
        console.error("Erro ao carregar detalhes do fornecedor:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchDetails();
  }, [fornecedor]);

  // Dados do fornecedor (mesclar props com dados da API)
  const fornecedorData = useMemo(() => {
    const cliente = detailData?.cliente || {};
    return {
      id: fornecedor?.id || cliente.id || "N/A",
      codigo: fornecedor?.codigo || cliente.codigo || fornecedor?.id || "N/A",
      nome: cliente.nome || fornecedor?.nome || "Fornecedor",
      cnpj: cliente.cnpj || fornecedor?.cnpj || "",
      razaoSocial: cliente.razaoSocial || cliente.nome || fornecedor?.nome || "",
      inscricaoEstadual: cliente.inscricaoEstadual || "N/A",
      segmento: fornecedor?.segmento || cliente.segmento || "Geral",
      cidade: cliente.cidade || fornecedor?.cidade || "-",
      uf: cliente.uf || fornecedor?.uf || "-",

      // Dados de compras
      totalCompras: cliente.faturamento || fornecedor?.faturamento || 0,
      quantidadePedidos: cliente.totalPedidos || 0,
      produtosUnicos: cliente.produtosUnicos || 0,
      ticketMedio: cliente.ticketMedio || 0,

      // Impostos
      icmsCredito: cliente.icms || fornecedor?.icms || 0,
      pisCofins: cliente.pisCofins || fornecedor?.pisCofins || 0,
      ipiCredito: cliente.ipi || 0,
      icmsST: cliente.icmsST || 0,

      // Projeções Reforma Tributária (8.5% CBS, 17.5% IBS)
      get cbs() {
        return this.totalCompras * 0.085;
      },
      get ibs() {
        return this.totalCompras * 0.175;
      },

      // Dados operacionais
      ultimaCompra: cliente.ultimaCompra || null,
      primeiraCompra: cliente.primeiraCompra || null,
    };
  }, [fornecedor, detailData]);

  // Produtos do fornecedor
  const produtos = useMemo(() => {
    return detailData?.produtos || [];
  }, [detailData]);

  // Histórico de notas
  const historico = useMemo(() => {
    return detailData?.historico || [];
  }, [detailData]);

  // Evolução mensal
  const evolucao = useMemo(() => {
    return detailData?.evolucao || [];
  }, [detailData]);

  // Distribuição de impostos para gráfico
  const impostos = useMemo(() => {
    return detailData?.impostos || [];
  }, [detailData]);

  // Função para formatar números
  const formatNumber = (num) => {
    if (!num || isNaN(num)) return "R$ 0,00";
    return `R$ ${Number(num).toLocaleString("pt-BR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  const formatCompact = (num) => {
    if (!num || isNaN(num)) return "R$ 0";
    if (num >= 1000000) {
      return `R$ ${(num / 1000000).toFixed(1)}M`;
    } else if (num >= 1000) {
      return `R$ ${(num / 1000).toFixed(0)}K`;
    }
    return `R$ ${num.toFixed(0)}`;
  };

  // Cálculos de métricas
  const totalCreditosTributarios =
    fornecedorData.icmsCredito +
    fornecedorData.pisCofins +
    fornecedorData.ipiCredito;

  const percentualCredito =
    fornecedorData.totalCompras > 0
      ? ((totalCreditosTributarios / fornecedorData.totalCompras) * 100).toFixed(1)
      : "0.0";

  const creditoPorPedido =
    fornecedorData.quantidadePedidos > 0
      ? totalCreditosTributarios / fornecedorData.quantidadePedidos
      : 0;

  // Loading state
  if (loading) {
    return (
      <Container theme={theme}>
        <SupplierDetailLoadingSkeleton 
          isDark={theme.body !== "#fafbfc"} 
          onVoltar={onVoltar}
        />
      </Container>
    );
  }

  return (
    <Container theme={theme}>
      <div className="content-wrapper">
        {/* Botão voltar acima do header */}
        <button className="back-button" onClick={onVoltar}>
          <FiArrowLeft />
          <span>Voltar</span>
        </button>

        {/* Header */}
        <div className="supplier-header">
          <div className="supplier-info-bar">
            <div className="supplier-icon">
              <FiPackage />
            </div>
            <div className="supplier-content">
              <div className="supplier-eyebrow">
                FORNECEDOR · {fornecedorData.cnpj || "N/A"}
              </div>
              <h2>{fornecedorData.nome}</h2>
              <div className="supplier-badges">
                <span className="badge supplier-tag">{fornecedorData.uf}</span>
                <span className="badge">{fornecedorData.quantidadePedidos} pedidos</span>
                <span className="badge">{fornecedorData.produtosUnicos} produtos</span>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs de Navegação */}
        <div className="tabs-container">
          <button
            className={`tab ${activeTab === "geral" ? "active" : ""}`}
            onClick={() => setActiveTab("geral")}
          >
            <FiLayers />
            <span>Geral</span>
          </button>
          <button
            className={`tab ${activeTab === "movimentacoes" ? "active" : ""}`}
            onClick={() => setActiveTab("movimentacoes")}
          >
            <FiRefreshCw />
            <span>Movimentações</span>
          </button>
          <button
            className={`tab ${activeTab === "reforma" ? "active" : ""}`}
            onClick={() => setActiveTab("reforma")}
          >
            <GoLaw />
            <span>Reforma Tributária</span>
          </button>
        </div>

        {/* Conteúdo da Aba Geral */}
        {activeTab === "geral" && (
          <>
            {/* Linha 1: Métricas de Compras */}
            <div className="kpi-grid kpi-grid-4">
              <div className="kpi-card primary">
                <div className="card-content">
                  <div className="thumb">
                    <FiDollarSign />
                  </div>
                  <div className="text">
                    <p>{formatCompact(fornecedorData.totalCompras)}</p>
                    <p>Total de Compras</p>
                  </div>
                </div>
              </div>
              <div className="kpi-card success">
                <div className="card-content">
                  <div className="thumb">
                    <FiShoppingCart />
                  </div>
                  <div className="text">
                    <p>{fornecedorData.quantidadePedidos}</p>
                    <p>Total de Pedidos</p>
                  </div>
                </div>
              </div>
              <div className="kpi-card">
                <div className="card-content">
                  <div className="thumb">
                    <FiDollarSign />
                  </div>
                  <div className="text">
                    <p>{formatCompact(fornecedorData.ticketMedio)}</p>
                    <p>Ticket Médio</p>
                  </div>
                </div>
              </div>
              <div className="kpi-card">
                <div className="card-content">
                  <div className="thumb">
                    <FiPackage />
                  </div>
                  <div className="text">
                    <p>{fornecedorData.produtosUnicos}</p>
                    <p>Produtos Únicos</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Linha 2: Créditos Tributários */}
            <div className="kpi-grid kpi-grid-4">
              <div className="kpi-card tax-icms">
                <div className="card-content">
                  <div className="thumb">
                    <FiDollarSign />
                  </div>
                  <div className="text">
                    <p>{formatCompact(fornecedorData.icmsCredito)}</p>
                    <p>ICMS Crédito</p>
                  </div>
                </div>
              </div>
              <div className="kpi-card tax-piscofins">
                <div className="card-content">
                  <div className="thumb">
                    <FiDollarSign />
                  </div>
                  <div className="text">
                    <p>{formatCompact(fornecedorData.pisCofins)}</p>
                    <p>PIS/COFINS</p>
                  </div>
                </div>
              </div>
              <div className="kpi-card">
                <div className="card-content">
                  <div className="thumb">
                    <FiDollarSign />
                  </div>
                  <div className="text">
                    <p>{formatCompact(fornecedorData.ipiCredito)}</p>
                    <p>IPI</p>
                  </div>
                </div>
              </div>
              <div className="kpi-card success">
                <div className="card-content">
                  <div className="thumb">
                    <FiDollarSign />
                  </div>
                  <div className="text">
                    <p>{formatCompact(totalCreditosTributarios)}</p>
                    <p>Total Impostos</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Linha 3: KPIs de Performance */}
            <div className="kpi-grid kpi-grid-3">
              <div className="kpi-card">
                <div className="card-content">
                  <div className="thumb">
                    <FiDollarSign />
                  </div>
                  <div className="text">
                    <p>{formatCompact(creditoPorPedido)}</p>
                    <p>Imposto por Pedido</p>
                  </div>
                </div>
              </div>
              <div className="kpi-card">
                <div className="card-content">
                  <div className="thumb">
                    <FiPercent />
                  </div>
                  <div className="text">
                    <p>{percentualCredito}%</p>
                    <p>Carga Tributária</p>
                  </div>
                </div>
              </div>
              <div className="kpi-card">
                <div className="card-content">
                  <div className="thumb">
                    <FiCalendar />
                  </div>
                  <div className="text">
                    <p>
                      {fornecedorData.ultimaCompra
                        ? new Date(fornecedorData.ultimaCompra).toLocaleDateString("pt-BR")
                        : "N/A"}
                    </p>
                    <p>Última Compra</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Linha 4: Gráficos Lado a Lado */}
            <div className="section-row">
              {/* Gráfico de Evolução Mensal */}
              {evolucao.length > 0 && (
                <div className="stats-card chart-card">
                  <div className="card-header">
                    <div className="header-title">
                      <FiTrendingUp />
                      <h4>Evolução Mensal de Compras</h4>
                    </div>
                    <div className="chart-legend">
                      <div className="legend-item">
                        <div className="legend-color" style={{ backgroundColor: "#3b82f6" }}></div>
                        <span>Compras</span>
                      </div>
                      <div className="legend-item">
                        <div className="legend-color" style={{ backgroundColor: "#10b981" }}></div>
                        <span>Impostos</span>
                      </div>
                    </div>
                  </div>
                  <div className="chart-container" style={{ height: "280px" }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={evolucao} margin={{ top: 10, right: 20, left: 10, bottom: 5 }}>
                        <defs>
                          <linearGradient id="gradVendas" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="#3b82f6" stopOpacity={1} />
                            <stop offset="100%" stopColor="#2563eb" stopOpacity={1} />
                          </linearGradient>
                          <linearGradient id="gradImpostos" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="#10b981" stopOpacity={0.8} />
                            <stop offset="100%" stopColor="#059669" stopOpacity={0.8} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke={theme.border} vertical={false} />
                        <XAxis
                          dataKey="mes"
                          tick={{ fill: theme.text, fontSize: 10, opacity: 0.7 }}
                          axisLine={false}
                          tickLine={false}
                        />
                        <YAxis
                          tick={{ fill: theme.text, fontSize: 9, opacity: 0.6 }}
                          axisLine={false}
                          tickLine={false}
                          width={60}
                          tickFormatter={(value) => formatCompact(value)}
                        />
                        <Tooltip
                          contentStyle={{
                            background: theme.card,
                            border: `1px solid ${theme.border}`,
                            borderRadius: "8px",
                          }}
                          formatter={(value, name) => [formatCompact(value), name === "vendas" ? "Compras" : "Impostos"]}
                        />
                        <Legend />
                        <Bar dataKey="vendas" name="Compras" fill="url(#gradVendas)" radius={[4, 4, 0, 0]} />
                        <Bar dataKey="impostos" name="Impostos" fill="url(#gradImpostos)" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}

              {/* Gráfico de Distribuição de Impostos */}
              {impostos.length > 0 && (
                <div className="stats-card">
                  <div className="card-header">
                    <div className="header-title">
                      <FiPercent />
                      <h4>Distribuição de Impostos</h4>
                    </div>
                  </div>
                  <div className="donut-wrapper">
                    <div className="donut-chart">
                      <svg width="200" height="200" viewBox="0 0 200 200">
                        {(() => {
                          const total = impostos.reduce((sum, imp) => sum + imp.value, 0);
                          let currentAngle = 0;
                          const radius = 80;
                          const innerRadius = 50;
                          const centerX = 100;
                          const centerY = 100;

                          return impostos.map((imposto, index) => {
                            if (total === 0) return null;
                            
                            const percentage = imposto.value / total;
                            const angle = percentage * 360;
                            const startAngle = currentAngle;
                            const endAngle = currentAngle + angle;
                            const startAngleRad = (startAngle * Math.PI) / 180;
                            const endAngleRad = (endAngle * Math.PI) / 180;
                            const largeArcFlag = angle > 180 ? 1 : 0;

                            const x1 = centerX + radius * Math.cos(startAngleRad);
                            const y1 = centerY + radius * Math.sin(startAngleRad);
                            const x2 = centerX + radius * Math.cos(endAngleRad);
                            const y2 = centerY + radius * Math.sin(endAngleRad);
                            const ix1 = centerX + innerRadius * Math.cos(startAngleRad);
                            const iy1 = centerY + innerRadius * Math.sin(startAngleRad);
                            const ix2 = centerX + innerRadius * Math.cos(endAngleRad);
                            const iy2 = centerY + innerRadius * Math.sin(endAngleRad);

                            const pathData = [
                              `M ${ix1} ${iy1}`,
                              `L ${x1} ${y1}`,
                              `A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2}`,
                              `L ${ix2} ${iy2}`,
                              `A ${innerRadius} ${innerRadius} 0 ${largeArcFlag} 0 ${ix1} ${iy1}`,
                              "Z",
                            ].join(" ");

                            const midAngle = startAngle + angle / 2;
                            const midAngleRad = (midAngle * Math.PI) / 180;
                            const labelRadius = (radius + innerRadius) / 2;
                            const labelX = centerX + labelRadius * Math.cos(midAngleRad);
                            const labelY = centerY + labelRadius * Math.sin(midAngleRad);

                            currentAngle += angle;

                            return (
                              <g key={index}>
                                <path d={pathData} fill={imposto.color} stroke="none" />
                                {percentage > 0.08 && (
                                  <text
                                    x={labelX}
                                    y={labelY}
                                    textAnchor="middle"
                                    dominantBaseline="middle"
                                    fill="#ffffff"
                                    fontSize="10"
                                    fontWeight="600"
                                  >
                                    {(percentage * 100).toFixed(0)}%
                                  </text>
                                )}
                              </g>
                            );
                          });
                        })()}
                      </svg>
                      <div className="donut-center">
                        <span className="donut-total">
                          {formatCompact(totalCreditosTributarios)}
                        </span>
                        <span className="donut-label">Total</span>
                      </div>
                    </div>
                    <div className="segments-list">
                      {impostos.map((imposto, index) => (
                        <div key={index} className="segment-item">
                          <div className="segment-header">
                            <div
                              className="segment-color"
                              style={{ backgroundColor: imposto.color }}
                            />
                            <span className="segment-name">{imposto.name}</span>
                            <span className="segment-revenue">{formatCompact(imposto.value)}</span>
                          </div>
                          <div className="segment-details">
                            <span className="segment-percentage">
                              {((imposto.value / totalCreditosTributarios) * 100).toFixed(1)}%
                            </span>
                          </div>
                          <div className="segment-bar">
                            <div
                              className="segment-progress"
                              style={{
                                width: `${totalCreditosTributarios > 0 ? (imposto.value / totalCreditosTributarios) * 100 : 0}%`,
                                backgroundColor: imposto.color,
                              }}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>

          </>
        )}

      {/* Conteúdo da Aba Movimentações */}
      {activeTab === "movimentacoes" && (
        <div className="history-grid">
          {/* Produtos Comprados */}
          <div className="history-table-wrapper">
            <div className="history-header-bar">
              <div className="header-title-group">
                <div className="header-icon"><FiPackage /></div>
                <h4>Produtos Comprados</h4>
              </div>
              <div className="header-meta">
                <span className="history-count">{produtos.length} produtos</span>
              </div>
            </div>
            <div className="history-table">
              <div className="table-header">
                <div>Produto</div>
                <div>Qtd</div>
                <div>ICMS</div>
                <div>Valor</div>
                <div>Part.</div>
              </div>
              {produtos.length === 0 && (
                <div className="history-empty">
                  Nenhum produto encontrado para este fornecedor.
                </div>
              )}
              {produtos.map((produto, index) => {
                const maxValor = produtos[0]?.valorTotal || 1;
                const barWidth = (produto.valorTotal / maxValor) * 100;
                const tagColor = produto.cor || ['#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#ef4444'][index % 5];

                return (
                  <div key={produto.id || index} className="table-row" style={{ "--product-color": tagColor }}>
                    <div>
                      <div className="product-info">
                        <div className="product-color" style={{ background: tagColor }}></div>
                        <div className="product-details">
                          <span className="product-name">{produto.nome}</span>
                          <span className="product-code">{produto.setor || `NCM ${produto.ncm}`}</span>
                        </div>
                      </div>
                    </div>
                    <div>
                      <span className="quantity-badge">{(produto.quantidade || 0).toLocaleString("pt-BR")} {produto.unidade || "UN"}</span>
                    </div>
                    <div>
                      <span className="value-text">{formatNumber(produto.valorIcms)}</span>
                    </div>
                    <div>
                      <span className="value-highlight">{formatNumber(produto.valorTotal)}</span>
                    </div>
                    <div>
                      <div className="participacao-bar-container">
                        <div className="participacao-bar" style={{ width: `${barWidth}%` }}></div>
                        <span className="participacao-value">{(produto.participacao || 0).toFixed(1)}%</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Notas Fiscais de Entrada */}
          <div className="history-table-wrapper">
            <div className="history-header-bar">
              <div className="header-title-group">
                <div className="header-icon"><FiFileText /></div>
                <h4>Notas Fiscais de Entrada</h4>
              </div>
              <div className="header-meta">
                <span className="history-count">{historico.length} registros</span>
              </div>
            </div>
            <div className="history-table">
              <div className="table-header">
                <div>Nota Fiscal</div>
                <div>Valor</div>
                <div>ICMS</div>
                <div>CFOP</div>
                <div>Data</div>
              </div>
              {historico.length === 0 && (
                <div className="history-empty">
                  Nenhuma nota fiscal encontrada para este fornecedor.
                </div>
              )}
              {historico.map((nota) => (
                <div key={nota.id} className="table-row">
                  <div>
                    <div className="product-info">
                      <div className="product-color" style={{ backgroundColor: '#10b981' }}></div>
                      <div className="product-details">
                        <span className="product-name">{nota.nf}</span>
                        <span className="product-code">{nota.status || "Autorizada"}</span>
                      </div>
                    </div>
                  </div>
                  <div>
                    <span className="value-highlight">{formatNumber(nota.valor)}</span>
                  </div>
                  <div>
                    <span className="value-text">{formatNumber(nota.valorIcms)}</span>
                  </div>
                  <div>
                    <span className="quantity-badge">{nota.cfop}</span>
                  </div>
                  <div>
                    <span className="value-text">{nota.data ? new Date(nota.data).toLocaleDateString('pt-BR') : '-'}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

        {/* Conteúdo da Aba Reforma */}
        {activeTab === "reforma" && (
          <div className="reforma-tab">
            {/* Timeline da Reforma Tributária */}
            <div className="reform-timeline">
              <button className="timeline-item active">
                <span className="timeline-year">2027</span>
                <span className="timeline-label">Início IVA Dual</span>
              </button>
              <button className="timeline-item">
                <span className="timeline-year">2028</span>
                <span className="timeline-label">Transição 0%</span>
              </button>
              <button className="timeline-item">
                <span className="timeline-year">2029</span>
                <span className="timeline-label">Transição 10%</span>
              </button>
              <button className="timeline-item">
                <span className="timeline-year">2030</span>
                <span className="timeline-label">Transição 20%</span>
              </button>
              <button className="timeline-item">
                <span className="timeline-year">2031</span>
                <span className="timeline-label">Transição 30%</span>
              </button>
              <button className="timeline-item">
                <span className="timeline-year">2032</span>
                <span className="timeline-label">Transição 40%</span>
              </button>
              <button className="timeline-item">
                <span className="timeline-year">2033</span>
                <span className="timeline-label">IVA Dual Pleno</span>
              </button>
            </div>

            {/* Linha 1: Situação Atual vs Reforma */}
            <div className="kpi-grid kpi-grid-4">
              <div className="kpi-card primary">
                <div className="card-content">
                  <div className="thumb">
                    <FiDollarSign />
                  </div>
                  <div className="text">
                    <p>{formatCompact(totalCreditosTributarios)}</p>
                    <p>Créditos Atuais</p>
                  </div>
                </div>
              </div>
              <div className="kpi-card">
                <div className="card-content">
                  <div className="thumb">
                    <FiPercent />
                  </div>
                  <div className="text">
                    <p>{percentualCredito}%</p>
                    <p>% Atual</p>
                  </div>
                </div>
              </div>
              <div className="kpi-card">
                <div className="card-content">
                  <div className="thumb">
                    <GoGraph />
                  </div>
                  <div className="text">
                    <p>{formatCompact(fornecedorData.cbs + fornecedorData.ibs)}</p>
                    <p>Projeção IVA Dual</p>
                  </div>
                </div>
              </div>
              <div className="kpi-card">
                <div className="card-content">
                  <div className="thumb">
                    <FiPercent />
                  </div>
                  <div className="text">
                    <p>26.0%</p>
                    <p>% Projetado</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Linha 2: Detalhamento CBS e IBS */}
            <div className="kpi-grid kpi-grid-4">
              <div className="kpi-card tax-cbs">
                <div className="card-content">
                  <div className="thumb">
                    <GoLaw />
                  </div>
                  <div className="text">
                    <p>{formatCompact(fornecedorData.cbs)}</p>
                    <p>CBS (8.5%)</p>
                  </div>
                </div>
              </div>
              <div className="kpi-card">
                <div className="card-content">
                  <div className="thumb">
                    <FiPercent />
                  </div>
                  <div className="text">
                    <p>8.5%</p>
                    <p>Alíquota CBS</p>
                  </div>
                </div>
              </div>
              <div className="kpi-card tax-ibs">
                <div className="card-content">
                  <div className="thumb">
                    <GoLaw />
                  </div>
                  <div className="text">
                    <p>{formatCompact(fornecedorData.ibs)}</p>
                    <p>IBS (17.5%)</p>
                  </div>
                </div>
              </div>
              <div className="kpi-card">
                <div className="card-content">
                  <div className="thumb">
                    <FiPercent />
                  </div>
                  <div className="text">
                    <p>17.5%</p>
                    <p>Alíquota IBS</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Linha 3: Análise de Impacto */}
            <div className="kpi-grid kpi-grid-2">
              <div className="kpi-card">
                <div className="card-content">
                  <div className="thumb">
                    <FiDollarSign />
                  </div>
                  <div className="text">
                    <p>
                      {formatCompact(
                        fornecedorData.cbs +
                          fornecedorData.ibs -
                          totalCreditosTributarios
                      )}
                    </p>
                    <p>Diferença em Créditos (R$)</p>
                  </div>
                </div>
              </div>
              <div className="kpi-card warning">
                <div className="card-content">
                  <div className="thumb">
                    <FiTrendingUp />
                  </div>
                  <div className="text">
                    <p>
                      {(() => {
                        const diff = 26.0 - parseFloat(percentualCredito);
                        return `${diff > 0 ? "+" : ""}${diff.toFixed(1)}%`;
                      })()}
                    </p>
                    <p>Variação do % de Crédito</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Linha 4: Comparativo Impostos Atuais vs Reforma - Donut Charts */}
            <div className="section-row">
              {/* Donut Chart - Sistema Atual */}
              <div className="stats-card chart-card">
                <div className="card-header">
                  <div className="header-title">
                    <GoGraph />
                    <h4>Sistema Atual - Distribuição de Créditos</h4>
                  </div>
                </div>
                <div className="donut-wrapper" style={{ padding: "30px 24px", minHeight: "280px" }}>
                  <div className="donut-chart">
                    {(() => {
                      const taxes = [
                        { name: "ICMS", value: fornecedorData.icmsCredito, color: "#f59e0b" },
                        { name: "PIS", value: fornecedorData.pisCredito, color: "#ef4444" },
                        { name: "COFINS", value: fornecedorData.cofinsCredito, color: "#10b981" },
                        { name: "IPI", value: fornecedorData.ipiCredito, color: "#8b5cf6" },
                      ];
                      const activeTaxes = taxes.filter((t) => t.value > 0);
                      const total = taxes.reduce((sum, t) => sum + t.value, 0);

                      let currentAngle = -90;
                      const radius = 80;
                      const innerRadius = 50;
                      const cx = 100, cy = 100;

                      return (
                        <>
                          <svg width="180" height="180" viewBox="0 0 200 200">
                            {activeTaxes.map((tax, index) => {
                              const percentage = tax.value / total;
                              const angle = percentage * 360;
                              const startAngle = currentAngle;
                              
                              if (angle >= 359.9) {
                                currentAngle += angle;
                                const path1 = `M ${cx} ${cy - radius} A ${radius} ${radius} 0 0 1 ${cx} ${cy + radius} A ${radius} ${radius} 0 0 1 ${cx} ${cy - radius} Z M ${cx} ${cy - innerRadius} A ${innerRadius} ${innerRadius} 0 0 0 ${cx} ${cy + innerRadius} A ${innerRadius} ${innerRadius} 0 0 0 ${cx} ${cy - innerRadius} Z`;
                                return (
                                  <g key={index}>
                                    <path d={path1} fill={tax.color} fillRule="evenodd" />
                                    <text x={cx} y={cy} textAnchor="middle" dominantBaseline="middle" fill="#fff" fontSize="11" fontWeight="600">100%</text>
                                  </g>
                                );
                              }

                              const startRad = (startAngle * Math.PI) / 180;
                              const endRad = ((startAngle + angle) * Math.PI) / 180;
                              const largeArc = angle > 180 ? 1 : 0;

                              const x1 = cx + radius * Math.cos(startRad);
                              const y1 = cy + radius * Math.sin(startRad);
                              const x2 = cx + radius * Math.cos(endRad);
                              const y2 = cy + radius * Math.sin(endRad);
                              const ix1 = cx + innerRadius * Math.cos(startRad);
                              const iy1 = cy + innerRadius * Math.sin(startRad);
                              const ix2 = cx + innerRadius * Math.cos(endRad);
                              const iy2 = cy + innerRadius * Math.sin(endRad);

                              const path = `M ${ix1} ${iy1} L ${x1} ${y1} A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2} L ${ix2} ${iy2} A ${innerRadius} ${innerRadius} 0 ${largeArc} 0 ${ix1} ${iy1} Z`;

                              const midAngle = startAngle + angle / 2;
                              const midRad = (midAngle * Math.PI) / 180;
                              const labelR = (radius + innerRadius) / 2;
                              const labelX = cx + labelR * Math.cos(midRad);
                              const labelY = cx + labelR * Math.sin(midRad);

                              currentAngle += angle;

                              return (
                                <g key={index}>
                                  <path d={path} fill={tax.color} />
                                  {percentage > 0.08 && (
                                    <text x={labelX} y={labelY} textAnchor="middle" dominantBaseline="middle" fill="#fff" fontSize="11" fontWeight="600">
                                      {(percentage * 100).toFixed(0)}%
                                    </text>
                                  )}
                                </g>
                              );
                            })}
                          </svg>
                          <div className="donut-center">
                            <span className="donut-total">{formatCompact(total)}</span>
                            <span className="donut-label">Total</span>
                          </div>
                        </>
                      );
                    })()}
                  </div>
                  <div className="categories-list">
                    {[
                      { name: "ICMS", value: fornecedorData.icmsCredito, color: "#f59e0b" },
                      { name: "PIS", value: fornecedorData.pisCredito, color: "#ef4444" },
                      { name: "COFINS", value: fornecedorData.cofinsCredito, color: "#10b981" },
                      { name: "IPI", value: fornecedorData.ipiCredito, color: "#8b5cf6" },
                    ].map((tax, index) => (
                      <div key={index} className="category-item">
                        <div className="category-color" style={{ backgroundColor: tax.color }}></div>
                        <span className="category-name">{tax.name}</span>
                        <span className="category-count">{formatCompact(tax.value)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Donut Chart - IVA Dual */}
              <div className="stats-card chart-card">
                <div className="card-header">
                  <div className="header-title">
                    <GoLaw />
                    <h4>Reforma Tributária - IVA Dual</h4>
                  </div>
                </div>
                <div className="donut-wrapper" style={{ padding: "30px 24px", minHeight: "280px" }}>
                  <div className="donut-chart">
                    {(() => {
                      const taxes = [
                        { name: "CBS", value: fornecedorData.cbs, color: "#f59e0b" },
                        { name: "IBS", value: fornecedorData.ibs, color: "#3b82f6" },
                      ];
                      const activeTaxes = taxes.filter((t) => t.value > 0);
                      const total = taxes.reduce((sum, t) => sum + t.value, 0);

                      let currentAngle = -90;
                      const radius = 80;
                      const innerRadius = 50;
                      const cx = 100, cy = 100;

                      return (
                        <>
                          <svg width="180" height="180" viewBox="0 0 200 200">
                            {activeTaxes.map((tax, index) => {
                              const percentage = tax.value / total;
                              const angle = percentage * 360;
                              const startAngle = currentAngle;
                              
                              if (angle >= 359.9) {
                                currentAngle += angle;
                                const path1 = `M ${cx} ${cy - radius} A ${radius} ${radius} 0 0 1 ${cx} ${cy + radius} A ${radius} ${radius} 0 0 1 ${cx} ${cy - radius} Z M ${cx} ${cy - innerRadius} A ${innerRadius} ${innerRadius} 0 0 0 ${cx} ${cy + innerRadius} A ${innerRadius} ${innerRadius} 0 0 0 ${cx} ${cy - innerRadius} Z`;
                                return (
                                  <g key={index}>
                                    <path d={path1} fill={tax.color} fillRule="evenodd" />
                                    <text x={cx} y={cy} textAnchor="middle" dominantBaseline="middle" fill="#fff" fontSize="11" fontWeight="600">100%</text>
                                  </g>
                                );
                              }

                              const startRad = (startAngle * Math.PI) / 180;
                              const endRad = ((startAngle + angle) * Math.PI) / 180;
                              const largeArc = angle > 180 ? 1 : 0;

                              const x1 = cx + radius * Math.cos(startRad);
                              const y1 = cy + radius * Math.sin(startRad);
                              const x2 = cx + radius * Math.cos(endRad);
                              const y2 = cy + radius * Math.sin(endRad);
                              const ix1 = cx + innerRadius * Math.cos(startRad);
                              const iy1 = cy + innerRadius * Math.sin(startRad);
                              const ix2 = cx + innerRadius * Math.cos(endRad);
                              const iy2 = cy + innerRadius * Math.sin(endRad);

                              const path = `M ${ix1} ${iy1} L ${x1} ${y1} A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2} L ${ix2} ${iy2} A ${innerRadius} ${innerRadius} 0 ${largeArc} 0 ${ix1} ${iy1} Z`;

                              const midAngle = startAngle + angle / 2;
                              const midRad = (midAngle * Math.PI) / 180;
                              const labelR = (radius + innerRadius) / 2;
                              const labelX = cx + labelR * Math.cos(midRad);
                              const labelY = cy + labelR * Math.sin(midRad);

                              currentAngle += angle;

                              return (
                                <g key={index}>
                                  <path d={path} fill={tax.color} />
                                  {percentage > 0.08 && (
                                    <text x={labelX} y={labelY} textAnchor="middle" dominantBaseline="middle" fill="#fff" fontSize="11" fontWeight="600">
                                      {(percentage * 100).toFixed(0)}%
                                    </text>
                                  )}
                                </g>
                              );
                            })}
                          </svg>
                          <div className="donut-center">
                            <span className="donut-total">{formatCompact(total)}</span>
                            <span className="donut-label">Total</span>
                          </div>
                        </>
                      );
                    })()}
                  </div>
                  <div className="categories-list">
                    {[
                      { name: "CBS", value: fornecedorData.cbs, color: "#f59e0b" },
                      { name: "IBS", value: fornecedorData.ibs, color: "#3b82f6" },
                    ].map((tax, index) => (
                      <div key={index} className="category-item">
                        <div className="category-color" style={{ backgroundColor: tax.color }}></div>
                        <span className="category-name">{tax.name}</span>
                        <span className="category-count">{formatCompact(tax.value)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

          </div>
        )}
      </div>
    </Container>
  );
}
